<template>
  <div class="bk-shell">
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <transition name="toast">
      <div
        v-if="baseToast.message"
        class="bk-toast"
        :class="'is-' + (baseToast.type || 'info')"
      >
        <span class="dot"></span>
        <span>{{ baseToast.message }}</span>
      </div>
    </transition>

    <transition v-if="isPermission" name="fade" mode="out-in">
      <!-- ==================== Form view ==================== -->
      <section key="form" v-if="isCreateUser" class="form-view">
        <div class="form-head">
          <button
            type="button"
            class="back-link"
            :disabled="isLoading"
            @click="isCreateUser = false"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
              <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>Banks</span>
          </button>
          <span class="head-sep">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span
            class="head-chip"
            :class="mode === 'create' ? 'head-chip-new' : 'head-chip-edit'"
          >{{ mode === 'create' ? 'New' : 'Editing' }}</span>
          <span v-if="mode === 'update'" class="head-name">
            {{ selectedUser.username }}
            <span v-if="selectedUser.upiId" class="head-name-sub">{{ selectedUser.upiId }}</span>
          </span>
          <span v-else class="head-name">Bank</span>

          <div v-if="mode === 'update'" class="form-head-actions">
            <div
              class="head-toggle"
              :class="{ 'head-toggle-updating': loginStatusUpdating }"
              title="Login Status"
            >
              <span class="toggle-caption">
                Login
                <strong :class="selectedUser.isLoggedIn ? 'is-on' : 'is-off'">{{ getUserLoginStatus }}</strong>
              </span>
              <base-toggle
                class="mb-0"
                :is-checked="selectedUser.isLoggedIn"
                @rsd-checkbox-changed="changeUserLoginStatus"
              ></base-toggle>
            </div>
            <div class="head-toggle" title="Bank status">
              <span class="toggle-caption">
                Status
                <strong :class="selectedUser.status ? 'is-on' : 'is-off'">{{ getUserStatus }}</strong>
              </span>
              <base-toggle
                class="mb-0"
                :is-checked="selectedUser.status"
                @rsd-checkbox-changed="changeUserStatus"
              ></base-toggle>
            </div>
            <button
              v-if="selectedUser.role !== 'admin'"
              type="button"
              class="head-icon-btn head-icon-danger"
              @click.stop="deleteUser(selectedUser)"
              title="Delete bank"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        <div v-if="isLoading" class="loading-block">
          <div class="loading-spinner"></div>
          <div class="loading-label">{{ getLoadingText }}</div>
        </div>
        <user-form
          v-else
          :mode="mode"
          :selected-user="selectedUser"
          @create-user="createUser"
        ></user-form>
      </section>

      <!-- ==================== List view ==================== -->
      <section key="list" v-else class="list-view">
        <header class="bk-header">
          <div>
            <div class="eyebrow">{{ isSubAdmin ? 'Branch' : 'Vendor' }}</div>
            <h1>Banks</h1>
            <p class="subtitle">Manage bank accounts used for pay-in — limits, statement uploads, and login state.</p>
          </div>
          <div class="header-actions">
            <button
              v-if="!isVendor && can('bank.create')"
              class="btn btn-primary"
              type="button"
              @click.stop="createBtnClicked"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
                <path d="M12 5v14M5 12h14" stroke-linecap="round" />
              </svg>
              New Bank
            </button>
          </div>
        </header>

        <!-- Filters -->
        <section class="bk-filters">
          <div class="search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
            </svg>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search by username, name, UPI ID, account, IFSC, phone, email…"
              class="search-input"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="search-clear"
              @click="searchQuery = ''"
              title="Clear"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <div v-if="canFilterByBranch" class="branch-select-wrap">
            <select v-model="branchFilter" class="branch-select">
              <option value="">All branches</option>
              <option v-for="br in branches" :key="br.id" :value="br.id">
                {{ br.name || br.username }}
              </option>
            </select>
            <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <div class="chip-group">
            <button
              v-for="opt in statusOptions"
              :key="opt.value"
              type="button"
              class="chip"
              :class="{ 'chip-active': statusFilter === opt.value }"
              @click="statusFilter = opt.value"
            >{{ opt.label }}</button>
          </div>
        </section>

        <!-- Body -->
        <div v-if="isLoading" class="bk-loading">
          <div class="loading-spinner"></div>
          <div class="loading-label">Loading banks…</div>
        </div>

        <div v-else-if="!hasUsers" class="empty-hero">
          <div class="empty-hero-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M3 10l9-6 9 6M4 10v9M20 10v9M8 10v9M12 10v9M16 10v9M2 21h20" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="empty-hero-title">No banks found</div>
          <div class="empty-hero-sub">
            {{ searchQuery || branchFilter || statusFilter !== 'active' ? 'Try adjusting your search or filters.' : 'Add your first bank account to get started.' }}
          </div>
          <button
            v-if="!isVendor && can('bank.create') && !searchQuery && !branchFilter && statusFilter === 'active'"
            type="button"
            class="btn btn-primary btn-inline"
            @click="createBtnClicked"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
              <path d="M12 5v14M5 12h14" stroke-linecap="round" />
            </svg>
            New Bank
          </button>
        </div>

        <div v-else class="bk-body">
          <transition-group
            tag="div"
            name="bank-list"
            class="bk-grid"
          >
            <user-item
              v-for="user in filteredUsers"
              :key="user.username"
              :user="user"
              :stats-override="bankStats[user.username]"
              @click.stop
              @edit-user="updateUser"
              @delete-user="deleteUser"
              @view-user-statistics="viewUserStatistics"
              @upload-statement="openUploadModal"
            ></user-item>
          </transition-group>

          <div class="pagination-wrap">
            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
          </div>
        </div>

        <!-- Bank report — teleported modern modal -->
        <teleport to="body">
          <transition name="modal-fade">
            <div v-if="openUserStatistics" class="modal-backdrop" @click.self="closeUserStatistics">
              <div class="modal-card modal-card-wide">
                <div class="modal-head">
                  <div>
                    <div class="modal-eyebrow">Bank report</div>
                    <div class="modal-title">{{ selectedUsername }}</div>
                    <div class="modal-sub">Pay-in activity across all statuses.</div>
                  </div>
                  <button type="button" class="modal-close" @click="closeUserStatistics">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                      <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                    </svg>
                  </button>
                </div>
                <div class="modal-body modal-body-scroll">
                  <user-report :username="selectedUsername"></user-report>
                </div>
              </div>
            </div>
          </transition>
        </teleport>

        <!-- Upload statement — teleported modern modal -->
        <teleport to="body">
          <transition name="modal-fade">
            <div v-if="showUploadModal" class="modal-backdrop" @click.self="closeUploadModal">
              <div class="modal-card">
                <div class="modal-head">
                  <div>
                    <div class="modal-eyebrow">Bank statement</div>
                    <div class="modal-title">Upload statement</div>
                    <div class="modal-sub">
                      Uploading for <strong>{{ uploadBankUsername }}</strong>
                    </div>
                  </div>
                  <button type="button" class="modal-close" @click="closeUploadModal">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                      <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                    </svg>
                  </button>
                </div>

                <div class="modal-body">
                  <div
                    @dragover.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="handleFileDrop"
                    :class="['drop-zone', { 'is-dragging': isDragging, 'has-file': selectedFile }]"
                    @click="$refs.fileInput.click()"
                  >
                    <input ref="fileInput" type="file" accept=".csv" @change="handleFileSelect" class="hidden-input" />
                    <div class="drop-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                        <path d="M12 3v14M6 9l6-6 6 6M4 21h16" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </div>
                    <div v-if="!selectedFile" class="drop-copy">
                      <div class="drop-title">Click to upload or drag & drop</div>
                      <div class="drop-sub">CSV file only</div>
                    </div>
                    <div v-else class="drop-file">
                      <div class="file-name">{{ selectedFile.name }}</div>
                      <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
                    </div>
                  </div>

                  <div
                    v-if="uploadResult"
                    class="upload-result"
                    :class="uploadResult.success ? 'is-success' : 'is-error'"
                  >
                    <div class="result-title">{{ uploadResult.message }}</div>
                    <div v-if="uploadResult.report" class="result-body">
                      <div class="result-row">
                        <span>Verified</span>
                        <strong>{{ uploadResult.report.summary.totalVerified }}</strong>
                      </div>
                      <div class="result-row">
                        <span>Unmatched</span>
                        <strong>{{ uploadResult.report.summary.totalUnmatched }}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="modal-foot">
                  <button type="button" class="btn btn-secondary" @click="closeUploadModal" :disabled="isUploading">
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="!selectedFile || isUploading"
                    @click="uploadStatement"
                  >
                    <span v-if="!isUploading">Upload</span>
                    <span v-else class="btn-loading">
                      <span class="spinner-mini"></span>
                      Uploading…
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </teleport>
      </section>
    </transition>

    <!-- Permission fallback -->
    <div v-else class="permission-block">
      <div class="permission-card">
        <div class="permission-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
          </svg>
        </div>
        <h2>You don't have permission to access this page</h2>
        <p>Head back to the dashboard.</p>
        <router-link to="/" class="btn btn-primary">Go to Home</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import UserItem from "../../components/users/UserItem.vue";
import UserForm from "../../components/users/UserForm.vue";
import UserReport from "../../components/users/UserReport.vue";
import http from "../../http-common";
import moment from "moment-timezone";
import Pagination from "../../components/shared/pagination.vue";

export default {
  name: "users",
  components: {
    UserItem,
    UserForm,
    Pagination,
    UserReport,
  },
  data() {
    // Use the configured display timezone (IST) so the date rolls over at
    // 12:00 AM IST instead of at the browser's local midnight (which can be
    // UTC = 5:30 AM IST in many deploy environments).
    const today = moment().tz(process.env.VUE_APP_TIMEZONE || "Asia/Kolkata").format("YYYY-MM-DD");
    return {
      isPermission: true,
      isLoading: false,
      error: null,
      isCreateUser: false,
      baseToast: { type: "", message: "" },
      mode: "create",
      selectedUser: Object,
      first: 10,
      page: 1,
      openUserStatistics: false,
      selectedUsername: "",
      showUploadModal: false,
      uploadBankUsername: "",
      selectedFile: null,
      isUploading: false,
      uploadResult: null,
      isDragging: false,
      loginStatusUpdating: false,
      statsDate: today,
      bankStats: {},
      searchQuery: "",
      statusFilter: "active",
      statusOptions: [
        { value: "all",      label: "All" },
        { value: "active",   label: "Active" },
        { value: "inactive", label: "Inactive" },
        { value: "deleted",  label: "Deleted" },
      ],
      branchFilter: "",
      branches: [],
      // Rename from `_searchTimer` (Vue reserves keys starting with underscore).
      searchTimer: null,
    };
  },
  watch: {
    page() {
      this.getUsers();
    },
    statsDate() {
      this.loadStats();
    },
    searchQuery() {
      // Debounce — refetch ~300ms after typing stops.
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.page !== 1) {
          this.page = 1;
        } else {
          this.getUsers();
        }
      }, 300);
    },
    statusFilter() {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.getUsers();
      }
    },
    branchFilter() {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.getUsers();
      }
    },
  },
  computed: {
    allUsers() {
      return this.$store.getters["users/users"];
    },
    filteredUsers() {
      return this.allUsers;
    },
    isVendor() {
      return this.$store.getters.role === "vendor" || this.$store.getters.role === "admin";
    },
    isSubAdmin() {
      return this.$store.getters.role === "subadmin";
    },
    canFilterByBranch() {
      const r = this.$store.getters.role;
      return ["vendor", "admin", "superadmin"].includes(r);
    },
    paginatorInfo() {
      return this.$store.getters["users/pagination"];
    },
    hasUsers() {
      return this.filteredUsers.length > 0;
    },
    getFormText() {
      return this.mode === "create" ? "Create Bank" : `Update Bank - ${this.selectedUser.username}`;
    },
    getUserStatus() {
      return this.selectedUser.status ? "Active" : "Inactive";
    },
    getUserLoginStatus() {
      return this.selectedUser.isLoggedIn ? "Logged In" : "Logged Out";
    },
    getLoadingText() {
      return this.mode === "create" ? "Creating…" : "Updating…";
    },
  },
  created() {
    this.getUsers();
    // Stats are per-bank day aggregates for the whole vendor — independent
    // of search/pagination, so they load once here (+ on statsDate change)
    // instead of serially blocking every list fetch inside the spinner.
    this.loadStats();
    if (this.canFilterByBranch) this.fetchBranches();
  },
  methods: {
    can(perm) { return this.$store.getters.hasPermission(perm); },
    setPage(page) { this.page = page; },
    formatFileSize(bytes) {
      if (!bytes) return "";
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    },
    async fetchBranches() {
      try {
        const r = await http.get("/users/subadmin");
        if (r.status === 200) this.branches = r.data || [];
      } catch (e) { /* dropdown just stays empty */ }
    },
    async getUsers() {
      this.isLoading = true;
      try {
        const params = {
          first: this.first,
          page: this.page,
          status: this.statusFilter,
          search: this.searchQuery && this.searchQuery.trim(),
          branchId: this.branchFilter || undefined,
        };
        await this.$store.dispatch("users/getUsers", params);
      } catch (error) {
        if (!error.status) this.isPermission = false;
        this.error = error.message || "Something failed!";
      }
      this.isLoading = false;
    },
    async loadStats() {
      try {
        const response = await http.get(`/dashboard/list-stats?type=bank&date=${this.statsDate}`);
        if (response.data.success) {
          const map = {};
          response.data.data.forEach(item => { map[item.username] = item; });
          this.bankStats = map;
        }
      } catch (error) {
        console.error("Error loading bank stats:", error);
      }
    },
    handleError() { this.error = null; },
    handleBaseToast() { this.baseToast = { type: "", message: "" }; },
    createBtnClicked() {
      this.mode = "create";
      this.isCreateUser = true;
      this.selectedUser = Object;
    },
    async createUser(data) {
      this.isLoading = true;
      try {
        if (this.mode === "create") {
          await this.$store.dispatch("users/createUser", data);
          this.baseToast.message = "Bank created successfully!";
        } else {
          await this.$store.dispatch("users/updateUser", data);
          this.baseToast.message = "Bank updated successfully!";
        }
        this.isCreateUser = false;
        this.baseToast.type = "success";
        setTimeout(() => { this.handleBaseToast(); }, 1000);
      } catch (error) {
        this.error = error.message || "Something failed!";
      }
      this.isLoading = false;
    },
    updateUser(user) {
      this.mode = "update";
      this.isCreateUser = true;
      this.selectedUser = user;
    },
    async deleteUser(user) {
      this.selectedUser = user;
      this.$swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this! Pending orders from this bank will be reassigned to you.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await this.$store.dispatch("users/deleteUser", this.selectedUser);
          this.$swal.fire("Deleted!", "Bank has been deleted.", "success");
          this.selectedUser = Object;
          this.isCreateUser = false;
        }
      });
    },
    async changeUserLoginStatus(status) {
      this.loginStatusUpdating = true;
      const username = this.selectedUser.username;
      try {
        const response = await http.put(`/users/${username}/${status}`);
        if (response.status === 200) {
          this.selectedUser.isLoggedIn = status;
        }
      } catch (e) {
        this.error = e.message || "Failed to update login status.";
      } finally {
        this.loginStatusUpdating = false;
      }
    },
    async changeUserStatus(status) {
      const data = { username: this.selectedUser.username, status };
      try {
        await this.$store.dispatch("users/changeUserStatus", data);
        const Toast = this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener("mouseenter", this.$swal.stopTimer);
            toast.addEventListener("mouseleave", this.$swal.resumeTimer);
          },
        });
        Toast.fire({
          icon: "success",
          title: status ? "User activated successfully" : "User inactivated successfully",
        });
        this.selectedUser.status = status;
      } catch (error) {
        this.error = error.message || "Something failed!";
      }
    },
    viewUserStatistics(username) {
      this.selectedUsername = username;
      this.openUserStatistics = true;
    },
    closeUserStatistics() {
      this.openUserStatistics = false;
      this.selectedUsername = "";
    },
    openUploadModal(username) {
      this.uploadBankUsername = username;
      this.showUploadModal = true;
      this.uploadResult = null;
      this.selectedFile = null;
    },
    closeUploadModal() {
      this.showUploadModal = false;
      this.uploadBankUsername = "";
      this.selectedFile = null;
      this.uploadResult = null;
    },
    handleFileSelect(event) {
      this.selectedFile = event.target.files[0];
      this.uploadResult = null;
    },
    handleFileDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.selectedFile = files[0];
        this.uploadResult = null;
      }
    },
    async uploadStatement() {
      if (!this.selectedFile) return;
      this.isUploading = true;
      const formData = new FormData();
      formData.append("bankStatement", this.selectedFile);
      formData.append("bankUsername", this.uploadBankUsername);

      try {
        const response = await http.post("/orders/upload-bank-statement-by-branch", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.uploadResult = response.data;
        if (response.data.success) {
          if (this.$toast) this.$toast.success("Bank statement uploaded successfully");
        } else {
          if (this.$toast) this.$toast.error(response.data.message || "Upload failed");
        }
        this.selectedFile = null;
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      } catch (error) {
        console.error("Upload error:", error);
        this.uploadResult = { success: false, message: error.response?.data?.message || "Upload failed" };
        if (this.$toast) this.$toast.error(this.uploadResult.message);
      } finally {
        this.isUploading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.bk-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}
.list-view, .form-view {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
}

/* ==================== Header ==================== */
.bk-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 600;
  margin-bottom: 4px;
}
.bk-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1b2a47;
  line-height: 1.1;
  margin: 0;
  letter-spacing: -0.01em;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
  line-height: 1.5;
}

/* ---------- Form head breadcrumb ---------- */
.form-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 4px;
  flex-wrap: wrap;
  min-height: 32px;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 6px;
  border: none;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.12s, background 0.12s;
  font-family: inherit;
}
.back-link:disabled { opacity: 0.5; cursor: not-allowed; }
.back-link:hover:not(:disabled) { color: #1B2A47; background: #eef2f7; }
.back-link svg { width: 16px; height: 16px; }

.head-sep { color: #cbd5e1; display: inline-flex; align-items: center; }
.head-sep svg { width: 14px; height: 14px; }

.head-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.head-chip-new {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 4px 10px -4px rgba(27, 42, 71, 0.45);
}
.head-chip-edit {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.head-name {
  font-size: 16px;
  font-weight: 700;
  color: #1b2a47;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.head-name-sub {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0;
  text-transform: none;
  font-family: "SFMono-Regular", Menlo, monospace;
}

.form-head-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.head-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #ede9df;
  transition: opacity 0.15s;
}
.head-toggle-updating { opacity: 0.55; pointer-events: none; }
.toggle-caption {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.02em;
  display: inline-flex;
  gap: 6px;
  align-items: baseline;
}
.toggle-caption strong { font-weight: 700; }
.is-on  { color: #059669; }
.is-off { color: #dc2626; }

.head-icon-btn {
  width: 34px; height: 34px;
  border-radius: 10px;
  border: 1px solid #ede9df;
  background: #fff;
  color: #475569;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
}
.head-icon-btn:hover { background: #f8fafc; border-color: #cbd5e1; }
.head-icon-btn svg { width: 16px; height: 16px; }
.head-icon-danger { color: #b91c1c; }
.head-icon-danger:hover { background: #fef2f2; border-color: #fecaca; color: #991b1b; }

/* ==================== Buttons ==================== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
  text-decoration: none;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.45);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-secondary:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
.btn-secondary:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-icon { width: 15px; height: 15px; }
.btn-inline { margin-top: 8px; }
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }

/* ==================== Filters ==================== */
.bk-filters {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 9px 34px 9px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  color: #0f172a;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.search-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.search-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 24px; height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.search-clear:hover { background: #f1f5f9; color: #475569; }
.search-clear svg { width: 14px; height: 14px; }

.branch-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.branch-select {
  appearance: none;
  -webkit-appearance: none;
  padding: 9px 32px 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  min-width: 180px;
  transition: border-color 0.15s, background 0.15s;
}
.branch-select:focus {
  border-color: #1B2A47;
  background: #fff;
}
.select-chevron {
  position: absolute;
  right: 10px;
  width: 14px; height: 14px;
  color: #94a3b8;
  pointer-events: none;
}

.chip-group { display: flex; gap: 4px; flex-wrap: wrap; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
  font-family: inherit;
}
.chip:not(.chip-active):hover { border-color: #cbd5e1; background: #f8fafc; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}

/* ==================== Loading + empty ==================== */
.bk-loading, .loading-block {
  padding: 50px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.loading-block { flex: 1; justify-content: center; padding: 60px 20px; gap: 14px; }
.loading-spinner {
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label { font-size: 13px; color: #64748b; }

.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}
.empty-hero-icon {
  width: 56px; height: 56px;
  border-radius: 14px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
}
.empty-hero-icon svg { width: 28px; height: 28px; }
.empty-hero-title { font-size: 15px; font-weight: 600; color: #1b2a47; }
.empty-hero-sub { font-size: 13px; color: #64748b; }

/* ==================== Body / grid ==================== */
.bk-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
}
.bk-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  position: relative;
}

.pagination-wrap {
  margin-top: auto;
  padding-top: 8px;
}

/* Bank-list transition preserved from original */
:deep(.bank-list-move),
:deep(.bank-list-enter-active),
:deep(.bank-list-leave-active) {
  transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
:deep(.bank-list-enter-from) { opacity: 0; transform: translateY(-12px); }
:deep(.bank-list-leave-to)   { opacity: 0; transform: translateX(40px) scale(0.97); }
:deep(.bank-list-leave-active) { position: absolute; left: 0; right: 0; z-index: 0; }

/* ==================== Permission fallback ==================== */
.permission-block {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}
.permission-card {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 20px;
  padding: 40px 32px;
  max-width: 420px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 12px 30px -20px rgba(27, 42, 71, 0.20);
}
.permission-icon {
  width: 56px; height: 56px;
  border-radius: 14px;
  background: #fef2f2;
  color: #dc2626;
  display: flex; align-items: center; justify-content: center;
}
.permission-icon svg { width: 28px; height: 28px; }
.permission-card h2 {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 0;
  line-height: 1.3;
}
.permission-card p {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 8px;
}

/* ==================== Toast ==================== */
.bk-toast {
  position: fixed;
  top: 24px; right: 24px;
  z-index: 60;
  background: #1b2a47; color: #fff;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 12px 30px -10px rgba(27, 42, 71, 0.4);
}
.bk-toast .dot { width: 8px; height: 8px; border-radius: 999px; background: #94a3b8; }
.bk-toast.is-success { background: #059669; }
.bk-toast.is-success .dot { background: #a7f3d0; }
.bk-toast.is-error { background: #dc2626; }
.bk-toast.is-error .dot { background: #fecaca; }

.toast-enter-active, .toast-leave-active { transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter-from, .toast-leave-to { transform: translateY(-12px); opacity: 0; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.24s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ==================== Upload modal ==================== */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-card {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px -20px rgba(15, 23, 42, 0.35);
  border: 1px solid #ede9df;
}
.modal-card-wide { max-width: 840px; }
.modal-body-scroll { overflow-y: auto; max-height: calc(100vh - 200px); }
.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f1ede2;
}
.modal-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 2px 0 4px;
  letter-spacing: -0.01em;
}
.modal-sub { font-size: 12.5px; color: #64748b; }
.modal-sub strong { color: #1b2a47; font-weight: 600; }
.modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.modal-close:hover { background: #f1f5f9; color: #1b2a47; }
.modal-close svg { width: 16px; height: 16px; }

.modal-body {
  padding: 20px 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.drop-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 14px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
}
.drop-zone:hover { border-color: #94a3b8; background: #f1f5f9; }
.drop-zone.is-dragging { border-color: #1B2A47; background: #eef2f7; }
.drop-zone.has-file { border-color: #10b981; background: #ecfdf5; }
.drop-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: #fff;
  color: #64748b;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid #e2e8f0;
}
.drop-icon svg { width: 22px; height: 22px; }
.drop-zone.has-file .drop-icon { color: #059669; border-color: #a7f3d0; }
.drop-title { font-size: 13px; font-weight: 600; color: #1b2a47; }
.drop-title .accent-word { color: #1B2A47; }
.drop-sub { font-size: 11.5px; color: #94a3b8; margin-top: 2px; }
.drop-file .file-name { font-size: 13px; font-weight: 600; color: #047857; word-break: break-all; }
.drop-file .file-size { font-size: 11.5px; color: #059669; margin-top: 2px; }
.hidden-input { display: none; }

.upload-result {
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.upload-result.is-success {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}
.upload-result.is-error {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}
.result-title { font-size: 13px; font-weight: 600; }
.result-body {
  display: flex;
  gap: 20px;
  padding-top: 4px;
  border-top: 1px solid currentColor;
  border-color: rgba(0, 0, 0, 0.08);
}
.result-row {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12.5px;
}
.result-row strong {
  font-weight: 700;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px 18px;
  border-top: 1px solid #f1ede2;
}
.spinner-mini {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .modal-card, .modal-fade-leave-active .modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from .modal-card { transform: translateY(-8px) scale(0.98); }
</style>
